// Demo transactions for testing cancel and amend flows
// Based on the transaction rules specification

export interface DemoTransaction {
  id: string
  mtoName: string
  mtoRef: string
  bankRef: string
  branchCode: string
  agentCode: string
  payoutType: 'FT' | 'IBFT' | 'OTC' | 'P2P' | 'B2B' | 'B2C'
  region: 'Ghana' | 'Nigeria' | 'Ivory Coast' | 'Senegal' | 'Other'
  transactionCategory: 'P2P' | 'B2B' | 'B2C'
  amountFCY: string
  currencyFCY: string
  amountPKR: string
  corridor: string
  purposeCode: string
  purposeText: string
  currentState: string
  lifecycleStatus: string
  createdAt: string
  updatedAt: string
  remitterName: string
  beneficiaryName: string
  beneficiaryIBAN: string
  beneficiaryBankName?: string
  amendAllowed: boolean
  cancelAllowed: boolean
  cancellationAction: string
  amendStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  cancelStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  railRefs: any
  stateHistory: any[]
  amendments?: Array<{
    id: string
    status: 'PENDING' | 'APPROVED' | 'REJECTED'
    reason: string
    timestamp: string
    changes: Array<{
      field: string
      oldValue: string
      newValue: string
    }>
  }>
  ePRCNo?: string
  ePRCDate?: string
}

const VALID_MTOS = [
  'RIA Money Transfer',
  'InstantCash',
  'MoneyGram',
  'Western Union',
]
const VALID_SUB_AGENTS = ['SA001', 'SA002', 'SA003', 'SA004']

export const DEMO_TRANSACTIONS: DemoTransaction[] = [
  // === AMEND-ELIGIBLE TRANSACTIONS (FT/IBFT + TITLE_FETCH + FAILED) - Most recent dates ===
  // FT Transaction - TITLE_FETCH (Failed) - Amend eligible
  {
    id: 'TXN-DEMO-012',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-012',
    bankRef: 'BNK-DEMO-012',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    region: 'Ghana',
    transactionCategory: 'P2P',
    amountFCY: '1500.00',
    currencyFCY: 'USD',
    amountPKR: '418,500',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 600000).toISOString(),
    updatedAt: new Date(Date.now() - 300000).toISOString(),
    remitterName: 'Robert Johnson',
    beneficiaryName: 'Imran Qureshi',
    beneficiaryIBAN: 'PK36MEZN0099000100120012',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
  },
  // IBFT Transaction - TITLE_FETCH (Failed) - Amend eligible
  {
    id: 'TXN-DEMO-013',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-013',
    bankRef: 'BNK-DEMO-013',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    region: 'Nigeria',
    transactionCategory: 'B2B',
    amountFCY: '850.00',
    currencyFCY: 'USD',
    amountPKR: '237,150',
    corridor: 'PK-AE',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 1200000).toISOString(),
    updatedAt: new Date(Date.now() - 600000).toISOString(),
    remitterName: 'Ahmed Ali',
    beneficiaryName: 'Fatima Khan',
    beneficiaryIBAN: 'PK36MEZN0099000100120013',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
  },
  // OTC Transaction - SCREENING (Failed) - Amend eligible
  {
    id: 'TXN-DEMO-014',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-014',
    bankRef: 'BNK-DEMO-014',
    branchCode: '0001',
    agentCode: 'SA001',
    payoutType: 'OTC',
    region: 'Ivory Coast',
    transactionCategory: 'B2C',
    amountFCY: '2000.00',
    currencyFCY: 'USD',
    amountPKR: '558,000',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'SCREENING',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 1800000).toISOString(),
    updatedAt: new Date(Date.now() - 900000).toISOString(),
    remitterName: 'John Smith',
    beneficiaryName: 'Ali Raza',
    beneficiaryIBAN: '',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
  },
  // === CANCEL-ELIGIBLE TRANSACTIONS (FT/IBFT + POSTING + PENDING_INQUIRY) ===
  // FT Transaction - FT_POST (Pending Inquiry) - Cancel eligible
  {
    id: 'TXN-DEMO-015',
    mtoName: 'Western Union',
    mtoRef: 'MTO-DEMO-015',
    bankRef: 'BNK-DEMO-015',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    region: 'Senegal',
    transactionCategory: 'P2P',
    amountFCY: '500.00',
    currencyFCY: 'USD',
    amountPKR: '139,500',
    corridor: 'PK-SA',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'FT_POST',
    lifecycleStatus: 'PENDING_INQUIRY',
    createdAt: new Date(Date.now() - 2400000).toISOString(),
    updatedAt: new Date(Date.now() - 1200000).toISOString(),
    remitterName: 'Sarah Connor',
    beneficiaryName: 'Tariq Jamil',
    beneficiaryIBAN: 'PK36MEZN0099000100120015',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSAL_REQUIRED',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
  },
  // IBFT Transaction - RAAST_POST (Pending Inquiry) - Cancel eligible
  {
    id: 'TXN-DEMO-016',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-016',
    bankRef: 'BNK-DEMO-016',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    region: 'Ghana',
    transactionCategory: 'B2B',
    amountFCY: '1200.00',
    currencyFCY: 'USD',
    amountPKR: '334,800',
    corridor: 'PK-US',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'RAAST_POST',
    lifecycleStatus: 'PENDING_INQUIRY',
    createdAt: new Date(Date.now() - 3000000).toISOString(),
    updatedAt: new Date(Date.now() - 1500000).toISOString(),
    remitterName: 'Michael Scott',
    beneficiaryName: 'Dwight Schrute',
    beneficiaryIBAN: 'PK36MEZN0099000100120016',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSAL_REQUIRED',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
  },
  // === IN_REVIEW TRANSACTIONS (SCREENING + IN_REVIEW) ===
  // FT Transaction - SCREENING (In Review) - Amend & Cancel eligible
  {
    id: 'TXN-DEMO-017',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-017',
    bankRef: 'BNK-DEMO-017',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    region: 'Nigeria',
    transactionCategory: 'P2P',
    amountFCY: '3000.00',
    currencyFCY: 'USD',
    amountPKR: '837,000',
    corridor: 'PK-AE',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'SCREENING',
    lifecycleStatus: 'IN_REVIEW',
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Bruce Wayne',
    beneficiaryName: 'Clark Kent',
    beneficiaryIBAN: 'PK36MEZN0099000100120017',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
  },
  // === SUCCESS TRANSACTIONS (For reference) ===
  {
    id: 'TXN-DEMO-018',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-018',
    bankRef: 'BNK-DEMO-018',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    region: 'Ivory Coast',
    transactionCategory: 'B2C',
    amountFCY: '450.00',
    currencyFCY: 'USD',
    amountPKR: '125,550',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 4200000).toISOString(),
    updatedAt: new Date(Date.now() - 2100000).toISOString(),
    remitterName: 'Tony Stark',
    beneficiaryName: 'Peter Parker',
    beneficiaryIBAN: 'PK36MEZN0099000100120018',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'NONE',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {
      RRN: 'RRN000000000018',
      STAN: '000018',
      RaastMsgId: 'RAAST18',
      UETR: 'UETR-18',
    },
    ePRCNo: 'EPRC-18',
    ePRCDate: new Date(Date.now() - 2100000).toISOString(),
    stateHistory: [],
  },
]
